/**
 * Supabase client singleton.
 *
 * Reads configuration from Vite env vars (`VITE_SUPABASE_URL` and
 * `VITE_SUPABASE_ANON_KEY`). Fails loudly in dev if either is missing —
 * better to crash on load than to silently write nothing to a nonexistent
 * backend.
 */

import { createClient, type SupabaseClient } from '@supabase/supabase-js';

let client: SupabaseClient | null = null;

export function getSupabaseClient(): SupabaseClient {
  if (client) return client;

  const url = import.meta.env.VITE_SUPABASE_URL;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

  if (!url || !anonKey) {
    throw new Error(
      'Supabase env vars missing. Copy .env.example to .env.local and fill in ' +
        'VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY from your Supabase ' +
        'project (Project Settings → API).',
    );
  }

  client = createClient(url, anonKey, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
    },
  });
  return client;
}

/** For tests and teardown — resets the cached singleton. */
export function resetSupabaseClient(): void {
  client = null;
}
